<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import BaseBottomSheet from '@/components/ui/BaseBottomSheet.vue'
import { useBottomSheet } from '@/composables/useBottomSheet'
import { useEventListener } from '@/composables/useEventListener'
import {
  FLOATING_SHEET_BOTTOM_OFFSET,
  mobileSheetFullHeight,
  mobileSheetMaxHeight,
} from '@/services/safeAreaService'
import { useUiStore } from '@/stores/uiStore'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'CanvasSheet' })

const uiStore = useUiStore()
const sheetRef = ref<InstanceType<typeof BaseBottomSheet> | null>(null)

// Snap heights mirror the Collection drawer (WorkspaceMobileToolbar): collapsed header = 58px,
// medium ≈ 52%, full = viewport − 88px.
const viewportHeight = ref(typeof window === 'undefined' ? 844 : window.innerHeight)
const sheetHeights = computed<Record<'peek' | 'mid' | 'full', number>>(() => {
  const height = viewportHeight.value
  return {
    peek: 58,
    mid: Math.floor(height * 0.52),
    full: mobileSheetFullHeight(height),
  }
})
// Map-first: opens collapsed (drag up to reveal).
const sheet = useBottomSheet({
  modes: ['peek', 'mid', 'full'] as const,
  heights: sheetHeights,
  initialMode: 'peek',
})

const sheetStyle = computed(() => ({
  height: `${sheet.height.value}px`,
  maxHeight: mobileSheetMaxHeight(FLOATING_SHEET_BOTTOM_OFFSET),
  bottom: FLOATING_SHEET_BOTTOM_OFFSET,
  zIndex: String(Z_INDEX.MOBILE_TOOLBAR),
  transition: sheet.isDragging.value ? 'none' : 'height 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}))

useEventListener(window, 'resize', () => {
  viewportHeight.value = window.innerHeight
})

// Publish the distance from the viewport bottom to the sheet's TOP (like the Collection
// drawer) so the create FAB and map inset sit above the sheet, not behind it.
//
// The sheet is bottom-anchored (CSS `bottom: TAB_BAR_OFFSET`), so during a snap the rendered
// `rect.top` lags behind by the 180ms height transition while `rect.bottom` stays put. Reading
// `rect.top` therefore measured a mid-animation height and dropped the FAB inside the sheet.
// Derive the stable bottom gap from `rect.bottom` instead, then add the *target* sheet height —
// this is transition-independent, so the FAB is correct immediately on every snap.
const publishHeight = () => {
  if (typeof window === 'undefined') return
  const rect = sheetRef.value?.rootEl?.getBoundingClientRect()
  if (!rect) return
  const bottomGap = window.innerHeight - rect.bottom
  uiStore.setMobileBottomPanelHeight(Math.round(bottomGap + sheet.height.value))
}
onMounted(() => void nextTick(publishHeight))
// Republish whenever the target height changes — fires every frame while dragging (transition
// is off) and once per snap. `publishHeight` uses the target height, so it stays correct even
// while the CSS height transition is still animating.
watch(() => sheet.height.value, publishHeight)

const showBody = computed(() => sheet.mode.value !== 'peek')
</script>

<template>
  <BaseBottomSheet
    ref="sheetRef"
    :sheet="sheet"
    title="Canvas"
    :sheet-style="sheetStyle"
    :show-body="showBody"
    testid="canvas-sheet"
  >
    <!-- Body (placeholder — content added later) -->
    <div class="px-4 pb-6">
      <div
        role="status"
        class="flex flex-col items-center gap-1 py-10 text-center text-sm text-neutral-500"
      >
        <span>Canvas is coming soon.</span>
      </div>
    </div>
  </BaseBottomSheet>
</template>
